import React, { Fragment, useState } from 'react';

import classNames from 'classnames';
import { format, parseISO } from 'date-fns';

import { formatDateWithTz } from 'Helpers/dateUtils';

import LoadingSpinner from 'CommonComponents/LoadingSpinner';
import PaginationInfo from 'CommonComponents/PaginationInfo';
import TableFilterModal from 'CommonComponents/TableFilterModal';
import TablePagination from 'CommonComponents/TablePagination';
import TableSearchInput from 'CommonComponents/TableSearchInput';

export default function ProjectDashboardActivitiesByCategory(props) {
    const [selectedActivityId, setSelectedActivityId] = useState(null);

    const outlook = props.activities || {};
    const rows = outlook.data || [];
    const currentPage = outlook.current_page || 1;
    const lastPage = outlook.last_page || 1;
    const perPage = outlook.per_page || 0;
    const total = outlook.total || 0;

    const from = total === 0 ? 0 : (currentPage - 1) * perPage + 1;
    const to = Math.min(currentPage * perPage, total);

    const toggleActionData = (activityId) => {
        if (activityId === selectedActivityId) {
            setSelectedActivityId(null);
        } else {
            setSelectedActivityId(activityId);
        }
    };

    const viewProjectActivityDetails = (activityId) => {
        window.location.href = props.viewProjectActivityDetailsUrl.replace('ACTIVITY_ID', activityId);
    };

    // Search and filter changes must return to the first page of the new result set.
    const applyFilters = () => {
        props.setPage(1);
        props.fetchData({ page: 1 });
    };

    const goToPage = (pageNumber) => {
        props.setPage(pageNumber);
        props.fetchData({ page: pageNumber });
    };

    const handleSearchKeyPress = (event) => {
        if (event.key === 'Enter') {
            applyFilters();
        }
    };

    return (
        <div className="table__card">
            <div className="tw-flex tw-items-end tw-justify-between tw-mb-6">
                <div className="tw-flex tw-flex-col tw-gap-y-1">
                    <h4 className="table__header">{$t('Outlook')}</h4>
                    {!props.loading && total > 0 && (
                        <div className="table__pagination-info">
                            <PaginationInfo from={from} to={to} total={total} />
                            {lastPage > 1 && (
                                <TablePagination lastPage={lastPage} currentPage={currentPage} pageChange={(data) => goToPage(+data.selected + 1)} />
                            )}
                        </div>
                    )}
                </div>
                <div className="tw-flex tw-gap-x-1.5">
                    <TableSearchInput searchValue={props.search} handleChange={props.changeSearch} handleKeyPress={handleSearchKeyPress} />
                    <TableFilterModal id="expenditures-table_filter-modal" onApply={applyFilters}>
                        <div>
                            <label htmlFor="due_date" className="tw-block tw-text-xs tw-mb-2.5">
                                {$t('Due')} ({$t('months')})
                            </label>
                            <input
                                type="text"
                                className="tw-w-full tw-max-w-full tw-h-8 tw-px-4 tw-bg-grey/50 tw-text-sm tw-font-semibold tw-border-0 tw-rounded-lg focus:tw-border-0 focus:tw-ring-0"
                                id="due_date"
                                value={props.periodInMonths}
                                onChange={(e) => props.changePeriod(e)}
                            />
                        </div>
                    </TableFilterModal>
                </div>
            </div>
            <div className="inbud-table_wrapper">
                <table className="inbud-table tw-table-fixed">
                    <thead>
                        <tr>
                            <th className="tw-min-w-48">{$t('Activity Description')}</th>
                            <th className="tw-min-w-48">{$t('Category')}</th>
                            <th className="tw-w-32">{$t('Owner')}</th>
                            <th className="tw-w-24">{$t('Start Date')}</th>
                            <th className="tw-w-32">{$t('Completion Date')}</th>
                            <th className="tw-w-24">{$t('Status')}</th>
                            <th className="tw-w-16">{$t('Actions')}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {props.loading ? (
                            <tr>
                                <td colSpan="7">
                                    <LoadingSpinner />
                                </td>
                            </tr>
                        ) : (
                            !!rows &&
                            rows.length > 0 && (
                                <>
                                    {rows.map((activity) => {
                                        return (
                                            <Fragment key={`activity-${activity.id}`}>
                                                <tr>
                                                    <td className="tw-cursor-pointer" onClick={() => viewProjectActivityDetails(activity.id)}>
                                                        <div className="tw-flex tw-flex-row tw-gap-x-3 tw-items-center">
                                                            <div
                                                                className={classNames('tw-min-w-4', 'tw-w-4', 'tw-h-4', 'tw-rounded-md', {
                                                                    'tw-bg-red-500': activity.rag_status == 'red' && !activity.is_complete,
                                                                    'tw-bg-amber-500': activity.rag_status == 'amber' && !activity.is_complete,
                                                                    'tw-bg-green-500': activity.rag_status == 'green' && !activity.is_complete,
                                                                    'tw-bg-blue-500': activity.is_complete,
                                                                })}
                                                            ></div>
                                                            <p className="tw-text-charcoal tw-text-sm">{activity.description}</p>
                                                        </div>
                                                    </td>
                                                    <td>{activity.category_description}</td>
                                                    <td>{activity.owner_name || '-'}</td>
                                                    <td className="inbud-date-cell">{format(parseISO(activity.start_date), 'dd-MM-yyyy')}</td>
                                                    <td className="inbud-date-cell">{format(parseISO(activity.target_completion_date), 'dd-MM-yyyy')}</td>
                                                    <td>{activity.status_label}</td>
                                                    {activity.total_actions_count > 0 ? (
                                                        <td className="tw-cursor-pointer inbud-numeric-cell" onClick={() => toggleActionData(activity.id)}>
                                                            {activity.total_actions_count}
                                                        </td>
                                                    ) : (
                                                        <td className="inbud-numeric-cell">{activity.total_actions_count}</td>
                                                    )}
                                                </tr>
                                                {selectedActivityId === activity.id && (
                                                    <DetailedActionDataRow selectedActivity={activity} data={activity.actions} />
                                                )}
                                            </Fragment>
                                        );
                                    })}
                                </>
                            )
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

function DetailedActionDataRow(props) {
    return (
        <tr>
            <td colSpan={7}>
                <div className="tw-bg-blue-25/50 tw-px-4 tw-py-5">
                    <div className="inbud-table_wrapper">
                        <table className="inbud-table">
                            <thead>
                                <tr>
                                    <th>{$t('RAG Status')}</th>
                                    <th>{$t('Description')}</th>
                                    <th>{$t('Owner')}</th>
                                    <th>{$t('Due Date')}</th>
                                    <th>{$t('Completed Date')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {props.data.map((action, index) => {
                                    return (
                                        <tr key={`action-data-${index}`}>
                                            <td>
                                                {action?.rag_status == 'blue' && <div className="tw-w-4 tw-h-4 tw-bg-blue-500 tw-rounded-md"></div>}
                                                {action?.rag_status == 'red' && <div className="tw-w-4 tw-h-4 tw-bg-red-500 tw-rounded-md"></div>}
                                                {action?.rag_status == 'amber' && <div className="tw-w-4 tw-h-4 tw-bg-amber-500 tw-rounded-md"></div>}
                                                {action?.rag_status == 'green' && <div className="tw-w-4 tw-h-4 tw-bg-green-500 tw-rounded-md"></div>}
                                                {!action?.rag_status || (action?.rag_status == '' && '-')}
                                            </td>
                                            <td>{action.description}</td>
                                            <td>{action.owner_name}</td>
                                            <td className="inbud-date-cell">{action.date ? formatDateWithTz(action.date, 'YYYY-MM-DD', 'DD/MM/YYYY') : '-'}</td>
                                            <td className="inbud-date-cell">
                                                {action.completed_date ? formatDateWithTz(action.completed_date, 'YYYY-MM-DD', 'DD/MM/YYYY') : '-'}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            </td>
        </tr>
    );
}
